import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const navItems = [
  { label: 'Ana Sayfa', path: '/' },
  { label: 'Hizmetler', path: '/hizmetler' },
  { label: 'Referanslar', path: '/referanslar' },
  { label: 'Hakkımızda', path: '/hakkimizda' },
  { label: 'İletişim', path: '/iletisim' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3' : 'py-5'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.02 }}>
            <Link
              to="/"
              className="flex items-center gap-2 group"
              data-testid="navbar-logo"
            >
              <div className="w-10 h-10 flex items-center justify-center relative">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <polygon
                    points="20,2 38,11 38,29 20,38 2,29 2,11"
                    fill="none"
                    stroke="#00E5FF"
                    strokeWidth="2"
                    className="group-hover:stroke-brand-purple transition-colors duration-300"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="6"
                    fill="#00E5FF"
                    className="group-hover:fill-brand-purple transition-colors duration-300"
                  />
                </svg>
              </div>
              <span className="font-display text-xl font-bold text-white">
                GEVEZ
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index + 0.3 }}
              >
                <Link
                  to={item.path}
                  className={`relative text-sm font-mono tracking-wider uppercase transition-colors duration-300
                    ${isActive(item.path) 
                      ? 'text-brand-cyan' 
                      : 'text-text-secondary hover:text-brand-cyan'
                    }`}
                  data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                >
                  {item.label}
                  {/* Active indicator */}
                  {isActive(item.path) && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-brand-cyan"
                      style={{ boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)' }}
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Link
              to="/iletisim"
              className={`hidden md:block px-6 py-2 border font-mono text-sm uppercase tracking-wider
                         transition-all duration-300
                         ${isActive('/iletisim')
                           ? 'bg-brand-cyan text-background-primary border-brand-cyan'
                           : 'border-brand-cyan text-brand-cyan hover:bg-brand-cyan hover:text-background-primary hover:shadow-[0_0_20px_rgba(0,229,255,0.5)]'
                         }`}
              data-testid="navbar-cta"
            >
              Teklif Al
            </Link>
          </motion.div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <motion.div
        initial={{ opacity: 0, x: '100%' }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          x: isMobileMenuOpen ? 0 : '100%',
        }}
        transition={{ duration: 0.3 }}
        className={`fixed inset-0 z-40 md:hidden bg-background-primary/95 backdrop-blur-xl ${
          isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col items-center justify-center h-full gap-8 pt-20">
          {navItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: 50 }}
              animate={{
                opacity: isMobileMenuOpen ? 1 : 0,
                x: isMobileMenuOpen ? 0 : 50,
              }}
              transition={{ delay: 0.1 * index }}
            >
              <Link
                to={item.path}
                className={`text-2xl font-display transition-colors
                  ${isActive(item.path) 
                    ? 'text-brand-cyan' 
                    : 'text-white hover:text-brand-cyan'
                  }`}
                data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: isMobileMenuOpen ? 1 : 0,
              y: isMobileMenuOpen ? 0 : 20,
            }}
            transition={{ delay: 0.5 }}
          >
            <Link
              to="/iletisim"
              className="mt-8 px-8 py-3 border-2 border-brand-cyan text-brand-cyan font-display text-lg
                         hover:bg-brand-cyan hover:text-background-primary transition-all duration-300"
              data-testid="mobile-cta"
            >
              Teklif Al
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default Navbar;
